import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  message,
  Modal,
  notification,
  Radio,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  CrownOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import creditIcon from "../../../../Assets/Icons/credit_card_clock.svg";
import { getModules } from "../../../../API/Config";
import {
  getCurrentSubscription,
  createSubscriptionPayment,
  resubmitSubscriptionPayment,
  initiateWiPaySubscriptionPayment,
} from "../../../../API/FleetManager/SubscriptionPayments";
import { savePaymentReturn } from "../../../../Utlis/PaymentReturn";
import OpenRequestBanner, {
  getOpenRequestState,
} from "../../../../Components/OpenRequestBanner";
import "./style.css";
import GenericModal from "../../../../Components/Modals/GenericModal";
import usePageAccess from "../../../../Utlis/usePageAccess";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.entries(data)
    .filter(([key]) => key !== "data")
    .flatMap(([key, value]) =>
      []
        .concat(value)
        .filter((msg) => typeof msg === "string")
        .map((msg) =>
          key === "non_field_errors"
            ? msg
            : `${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_/g, " ")}: ${msg}`,
        ),
    );

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const MODULE_ICONS_BY_CODE = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];
const WIPAY_MIN_AMOUNT = 114.7;

const formatDays = (count) => `${count} day${Number(count) === 1 ? "" : "s"}`;

const formatMoney = (currency, value) =>
  `${currency} ${(parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const requestAddonQuote = async (slots, moduleIds) => {
  const res = await getCurrentSubscription({
    requested_vehicle_slots: slots || 0,
    ...(moduleIds.length && { module_ids: moduleIds.join(",") }),
  });
  return res?.data?.data?.addon_quote || null;
};

const getFileName = (url) => {
  const last = String(url || "").split("?")[0].split("/").pop();
  try {
    return decodeURIComponent(last) || "View file";
  } catch {
    return last || "View file";
  }
};

const buildModuleOption = (mod, isAnnual) => {
  const fallbackIcon =
    MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
  const iconSrc = mod.icon ? resolveAvatarUrl(mod.icon) : fallbackIcon;
  const rawPrice = isAnnual
    ? mod.annual_price_per_vehicle
    : mod.monthly_price_per_vehicle;
  const price = parseFloat(rawPrice);
  const isInactive = mod.status ? mod.status !== "active" : false;

  return {
    id: mod.id,
    title: mod.name,
    description: mod.description,
    price: rawPrice
      ? price
        ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle`
        : "Free"
      : undefined,
    icon: <img src={iconSrc} alt={mod.name} />,
    disabled: isInactive,
    tag: isInactive ? "Coming Soon" : undefined,
  };
};

const Subscription = () => {
  const { canEdit } = usePageAccess("subscription-payments");
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedPaymentMethod = Form.useWatch("payment_method", form);
  const watchedAmount = Form.useWatch("amount", form);
  const fileInputRef = useRef(null);

  const [assignedModulesRaw, setAssignedModulesRaw] = useState([]);
  const [vehicles, setVehicles] = useState(1);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [currentPricingPeriod, setCurrentPricingPeriod] = useState(null);
  const [pendingCycle, setPendingCycle] = useState(null);

  const [isUpgrading, setIsUpgrading] = useState(false);
  const [catalogModules, setCatalogModules] = useState([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [upgradeVehicles, setUpgradeVehicles] = useState(1);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [wipayLoading, setWipayLoading] = useState(false);
  const [openRequest, setOpenRequest] = useState(null);
  const [addonPricing, setAddonPricing] = useState(null);
  const [addonQuote, setAddonQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [openingPay, setOpeningPay] = useState(false);
  const upgradeFallbackMessage =
    "You already have a subscription request under review. You can send a new one once it has been reviewed.";
  const {
    canCreate: canCreateRequest,
    message: blockedMessage,
    resubmitId,
  } = getOpenRequestState(openRequest, upgradeFallbackMessage);
  const canUpgrade = canCreateRequest || resubmitId != null;
  const resubmitRequest = resubmitId != null ? openRequest?.request : null;
  const existingProofUrl = resubmitRequest?.proof_file
    ? resolveAvatarUrl(resubmitRequest.proof_file)
    : null;

  useEffect(() => {
    const fetchCurrent = async () => {
      try {
        const res = await getCurrentSubscription();
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          const assigned = Array.isArray(data.assigned_modules)
            ? data.assigned_modules
            : [];

          setAssignedModulesRaw(assigned);
          setVehicles(data.vehicle_limit || 1);
          setOpenRequest(data.open_request || null);
          setAddonPricing(data.addon_pricing || null);
          const currentCycle =
            data.billing_cycle || data.addon_pricing?.billing_cycle;
          if (currentCycle) {
            const period =
              currentCycle === "annual" ? "Annually Pricing" : "Monthly Pricing";
            setCurrentPricingPeriod(period);
            setPricingPeriod(period);
          }
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load current subscription."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(
            err?.response?.data,
            "Failed to load current subscription.",
          ),
        );
      }
    };

    fetchCurrent();
  }, []);

  useEffect(() => {
    if (!isUpgrading || catalogModules.length) return;

    const fetchModules = async () => {
      try {
        const res = await getModules();
        const modules = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        setCatalogModules(modules);
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load modules."),
        );
      }
    };

    fetchModules();
  }, [isUpgrading, catalogModules.length]);

  const isAddon = !!addonPricing;
  const isAddonUpgrade = isAddon && isUpgrading;
  const addonCurrency = addonPricing?.currency || "JMD";
  const addonModulesById = Object.fromEntries(
    (addonPricing?.modules || []).map((mod) => [mod.id, mod]),
  );
  const lockedModuleIds = (addonPricing?.modules || [])
    .filter((mod) => mod.is_assigned)
    .map((mod) => mod.id);
  const newModuleIds = selectedModuleIds.filter(
    (id) => !lockedModuleIds.includes(id),
  );
  const newModuleKey = newModuleIds.join(",");

  useEffect(() => {
    if (!isAddonUpgrade) return;

    if (!upgradeVehicles && !newModuleKey) {
      setAddonQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);
    const timer = setTimeout(async () => {
      try {
        const quote = await requestAddonQuote(
          upgradeVehicles,
          newModuleKey ? newModuleKey.split(",").map(Number) : [],
        );
        if (!cancelled) {
          setAddonQuote(quote);
          setQuoteError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAddonQuote(null);
          setQuoteError(
            getErrorMessage(
              err?.response?.data,
              "Couldn't get the price for this add-on.",
            ),
          );
        }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAddonUpgrade, upgradeVehicles, newModuleKey]);

  const isAnnual = pricingPeriod === "Annually Pricing";
  const moduleOptions = (isUpgrading ? catalogModules : assignedModulesRaw).map(
    (mod) => {
      // The "Selected Module" view shows the plain admin-set rate; only the
      // add-on upgrade screen shows the prorated price and its breakdown.
      const option = buildModuleOption(mod, isAnnual);
      const prorated = addonModulesById[mod.id];
      if (!isAddonUpgrade || !prorated) return option;

      const priceDetails = [
        {
          label: "Original Price",
          value: `${formatMoney(
            prorated.currency || addonCurrency,
            prorated.price_per_vehicle,
          )}/vehicle`,
        },
        ...(addonPricing?.omitted_days != null
          ? [
              {
                label: "Omitted Days",
                value: formatDays(addonPricing.omitted_days),
              },
            ]
          : []),
      ];

      return {
        ...option,
        price: `${formatMoney(
          prorated.currency || addonCurrency,
          prorated.prorated_price_per_vehicle,
        )}/vehicle`,
        priceDetails,
        tag: prorated.is_assigned ? "Current" : option.tag,
      };
    },
  );

  const handleModuleChange = (ids) => {
    setSelectedModuleIds(
      isAddon ? [...new Set([...lockedModuleIds, ...ids])] : ids,
    );
  };

  const addonTotalText = !isAddonUpgrade
    ? undefined
    : quoteLoading
      ? "..."
      : addonQuote
        ? formatMoney(addonQuote.currency || addonCurrency, addonQuote.amount)
        : "-";
  const wipayTooSmall =
    isAddon && Number(watchedAmount || 0) < WIPAY_MIN_AMOUNT;
  const selectedModulesData = catalogModules.filter((mod) =>
    selectedModuleIds.includes(mod.id),
  );

  const calculateAmount = (cycle) => {
    const perVehicle = selectedModulesData.reduce(
      (sum, mod) =>
        sum +
        (parseFloat(
          cycle === "annual"
            ? mod.annual_price_per_vehicle
            : mod.monthly_price_per_vehicle,
        ) || 0),
      0,
    );
    return Number((perVehicle * (upgradeVehicles || 0)).toFixed(2));
  };

  const handleBack = () => {
    if (isUpgrading) {
      setIsUpgrading(false);
      if (currentPricingPeriod) setPricingPeriod(currentPricingPeriod);
      setSelectedModuleIds([]);
      setAddonQuote(null);
      setQuoteError(null);
      return;
    }
    navigate(-1);
  };

  const handleUpgradeClick = () => {
    if (isAddon) {
      const requestedIds = (resubmitRequest?.requested_modules || []).map(
        (mod) => mod.id,
      );
      setSelectedModuleIds([
        ...new Set([...lockedModuleIds, ...requestedIds]),
      ]);
      setUpgradeVehicles(resubmitRequest?.requested_vehicle_slots || 0);
    } else if (resubmitRequest) {
      const moduleIds = (resubmitRequest.requested_modules || []).map(
        (mod) => mod.id,
      );
      setSelectedModuleIds(moduleIds);
      if (resubmitRequest.requested_vehicle_slots) {
        setUpgradeVehicles(resubmitRequest.requested_vehicle_slots);
      }
      if (resubmitRequest.billing_cycle) {
        setPricingPeriod(
          resubmitRequest.billing_cycle === "annual"
            ? "Annually Pricing"
            : "Monthly Pricing",
        );
      }
    }
    setIsUpgrading(true);
  };

  const handlePricingPeriodChange = (period) => {
    if (!isAddonUpgrade || period === currentPricingPeriod) {
      setPricingPeriod(period);
      return;
    }
    setPendingCycle(period === "Annually Pricing" ? "annual" : "monthly");
  };

  const pendingCycleLabel = pendingCycle === "annual" ? "Annual" : "Monthly";
  const currentCycleLabel = pendingCycle === "annual" ? "monthly" : "annual";
  const switchUntil = addonPricing?.prorated_until || "";

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
      form.setFields([{ name: "proof_file", errors: [] }]);
    }
    e.target.value = null;
  };

  const getResubmitPrefill = () => {
    if (!resubmitRequest) return {};
    const previousDate = resubmitRequest.payment_date
      ? dayjs(resubmitRequest.payment_date)
      : null;
    return {
      payment_method: resubmitRequest.payment_method || undefined,
      payment_date:
        previousDate && previousDate.isValid() ? previousDate : undefined,
    };
  };

  const handleOpenAddonPay = async () => {
    if (!upgradeVehicles && !newModuleIds.length) {
      message.error("Add at least one extra vehicle slot or a new module.");
      return;
    }

    setOpeningPay(true);
    try {
      const quote = await requestAddonQuote(upgradeVehicles, newModuleIds);
      if (!quote) {
        message.error("Couldn't get the price for this add-on.");
        return;
      }
      setAddonQuote(quote);
      setQuoteError(null);
      form.setFieldsValue({
        billing_cycle: addonPricing.billing_cycle,
        amount: Number(quote.amount),
        ...getResubmitPrefill(),
      });
      setIsPayModalOpen(true);
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Couldn't get the price for this add-on.",
        ),
      );
    } finally {
      setOpeningPay(false);
    }
  };

  const handleOpenPay = () => {
    if (isAddon) {
      handleOpenAddonPay();
      return;
    }
    if (!selectedModuleIds.length) {
      message.error("Please select at least one module to upgrade.");
      return;
    }
    if (!upgradeVehicles || upgradeVehicles < 1) {
      message.error("Please enter number of vehicles.");
      return;
    }
    const cycle = isAnnual ? "annual" : "monthly";
    form.setFieldsValue({
      billing_cycle: cycle,
      amount: calculateAmount(cycle),
      ...getResubmitPrefill(),
    });
    setIsPayModalOpen(true);
  };

  const payCycle = isAddon
    ? addonPricing?.billing_cycle
    : isAnnual
      ? "annual"
      : "monthly";

  const paymentModuleIds = isAddon ? newModuleIds : selectedModuleIds;
  const paymentBillingCycle = (values) =>
    isAddon ? addonPricing.billing_cycle : values.billing_cycle;

  const handleCancelPay = () => {
    setIsPayModalOpen(false);
    form.resetFields();
    setProofFile(null);
  };

  const handleSubmitPayment = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }

    const formData = new FormData();
    formData.append("amount", values.amount);
    formData.append("payment_method", values.payment_method);
    formData.append("payment_date", values.payment_date.format("YYYY-MM-DD"));
    if (proofFile) {
      formData.append("proof_file", proofFile);
    }
    formData.append("requested_vehicle_slots", upgradeVehicles || 0);
    formData.append("billing_cycle", paymentBillingCycle(values));
    paymentModuleIds.forEach((id) => {
      formData.append("module_ids", id);
    });

    setSubmitting(true);
    try {
      const res =
        resubmitId != null
          ? await resubmitSubscriptionPayment(resubmitId, formData)
          : await createSubscriptionPayment(formData);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message:
            resubmitId != null ? "Request Resubmitted" : "Request Submitted",
          description:
            res?.data?.message ||
            "Subscription payment submitted successfully!",
        });
        navigate("/subscription-payments");
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to submit subscription payment."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to submit subscription payment.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleWiPayPayment = async () => {
    let values;
    try {
      values = await form.validateFields(["amount", "billing_cycle"]);
    } catch (err) {
      return;
    }

    if (!values.amount || Number(values.amount) <= 0) {
      message.error("Please enter a valid amount.");
      return;
    }

    setWipayLoading(true);
    try {
      const res = await initiateWiPaySubscriptionPayment({
        amount: String(values.amount),
        module_ids: paymentModuleIds.map(String),
        billing_cycle: paymentBillingCycle(values),
        requested_vehicle_slots: upgradeVehicles || 0,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const checkoutUrl = res?.data?.data?.checkout_url;

        if (checkoutUrl) {
          notification.success({
            message: "Redirecting to WiPay",
            description:
              res?.data?.message || "WiPay checkout initiated successfully",
            placement: "topRight",
          });
          savePaymentReturn({ pathname: "/subscription-payments" });
          window.location.href = checkoutUrl;
          return;
        }

        notification.error({
          message: "WiPay Payment Failed",
          description: "WiPay checkout URL was not returned.",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "WiPay Payment Failed",
          description: getErrorMessage(
            res?.data,
            "Failed to initiate WiPay payment.",
          ),
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "WiPay Payment Failed",
        description: getErrorMessage(
          err?.response?.data,
          "Failed to initiate WiPay payment.",
        ),
        placement: "topRight",
      });
    }
    setWipayLoading(false);
  };

  const isBusy = submitting || wipayLoading;

  return (
    <div className="subscription-page">
      <div className="top-row">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
        {canEdit && !isUpgrading && (
          <Button
            type="primary"
            onClick={handleUpgradeClick}
            disabled={!canUpgrade}
            title={canUpgrade ? undefined : blockedMessage}
          >
            {resubmitId != null ? "Resubmit Upgrade" : "Upgrade"}
          </Button>
        )}
      </div>

      <OpenRequestBanner
        openRequest={openRequest}
        label="Subscription request"
        fallbackMessage={upgradeFallbackMessage}
      />

      <ModuleSelctorCards
        options={moduleOptions}
        title={isUpgrading ? "Select Module to Upgrade" : "Selected Module"}
        headerExtra={
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            className="custom-segmented-control"
            value={pricingPeriod}
            onChange={(e) => handlePricingPeriodChange(e.target.value)}
            options={PRICING_TABS.map((tab) => ({
              label: tab.replace(" Pricing", ""),
              value: tab,
              disabled:
                !isUpgrading &&
                !!currentPricingPeriod &&
                tab !== currentPricingPeriod,
            }))}
          />
        }
        headerSubtitle={
          isAddonUpgrade ? (
            <>
              <p className="module-subtitle">
                Add vehicle slots or new modules to your current{" "}
                {addonPricing.billing_cycle === "annual" ? "annual" : "monthly"}{" "}
                plan. You only pay for the{" "}
                <strong>{formatDays(addonPricing.remaining_days)}</strong> left
                until{" "}
                <strong>{addonPricing.prorated_until}</strong>. The full price
                applies at renewal.
              </p>
              {quoteError && (
                <p className="module-subtitle addon-quote-error">
                  {quoteError}
                </p>
              )}
            </>
          ) : (
            canEdit &&
            !isUpgrading &&
            canUpgrade && (
              <p className="module-subtitle">
                Click "Upgrade" above to browse all modules and update your
                subscription.
              </p>
            )
          )
        }
        allowSelection={isUpgrading}
        footerLabel={
          isAddonUpgrade
            ? "Prorated Rate"
            : isAnnual
              ? "Annual Rate"
              : "Monthly Rate"
        }
        showFooter={true}
        selectedIds={
          isUpgrading ? selectedModuleIds : assignedModulesRaw.map((m) => m.id)
        }
        onChange={isUpgrading ? handleModuleChange : undefined}
        multiple={true}
        showVehicleInput={true}
        vehicleCount={isUpgrading ? upgradeVehicles : vehicles}
        onVehicleChange={isUpgrading ? setUpgradeVehicles : undefined}
        vehicleLabel={
          isAddonUpgrade
            ? `Extra Vehicle Slots (current: ${addonPricing.current_vehicle_limit})`
            : undefined
        }
        vehicleMin={isAddonUpgrade ? 0 : 1}
        totalText={addonTotalText}
      />

      {canEdit && isUpgrading && (
        <div className="btns-row">
          <Button
            type="primary"
            onClick={handleOpenPay}
            loading={openingPay}
            disabled={!canUpgrade}
            title={canUpgrade ? undefined : blockedMessage}
          >
            Pay
          </Button>
        </div>
      )}

      <GenericModal
        open={!!pendingCycle}
        title={`Switch to ${pendingCycleLabel} Billing?`}
        subtitle={`This renews your subscription on the ${pendingCycleLabel.toLowerCase()} plan. Your current ${currentCycleLabel} period stays active${switchUntil ? ` until ${switchUntil}` : ""}, and the ${pendingCycleLabel.toLowerCase()} plan starts right after it at the full ${pendingCycleLabel.toLowerCase()} price.`}
        highlightText={switchUntil}
        showReason={false}
        confirmText="Continue to Renewal"
        confirmIcon={<CrownOutlined />}
        cancelText="Cancel"
        onCancel={() => setPendingCycle(null)}
        onConfirm={() => {
          const cycle = pendingCycle;
          setPendingCycle(null);
          navigate(`/my-subscription?renew=1&billing_cycle=${cycle}`);
        }}
      />

      <Modal
        className="generic-modal subscription-pay-modal"
        open={isPayModalOpen}
        onCancel={handleCancelPay}
        title={null}
        footer={null}
        centered
        closable={!isBusy}
        maskClosable={!isBusy}
      >
        <div className="top-gradient"></div>

        <div className="modal-body-wrapper">
          <div className="modal-header">
            <div
              className="modal-icon-wrapper"
              style={{ backgroundColor: "#f3f4f6" }}
            >
              <img src={creditIcon} alt="pay" />
            </div>
            <span className="modal-title">
              {isAddon
                ? "Pay for Add-on"
                : `Pay for ${
                    selectedModulesData.map((mod) => mod.name).join(", ") ||
                    "Module"
                  }`}
            </span>
          </div>

          {isAddon && addonQuote && (
            <div className="addon-breakdown">
              <div className="addon-breakdown__note">
                Prorated for {formatDays(addonQuote.remaining_days)} left, until{" "}
                {addonQuote.prorated_until}. The full price applies at renewal.
              </div>
              {(addonQuote.lines || []).map((line) => (
                <div className="addon-breakdown__line" key={line.module_id}>
                  <div className="addon-breakdown__label">
                    <span className="addon-breakdown__name">
                      {line.module_name}
                      {line.is_new_module && (
                        <span className="addon-breakdown__tag">New</span>
                      )}
                    </span>
                    <span className="addon-breakdown__calc">
                      {line.vehicles} vehicle{Number(line.vehicles) === 1 ? "" : "s"} ×{" "}
                      {formatMoney(
                        addonQuote.currency || addonCurrency,
                        line.prorated_price_per_vehicle,
                      )}
                    </span>
                  </div>
                  <strong>
                    {formatMoney(
                      addonQuote.currency || addonCurrency,
                      line.amount,
                    )}
                  </strong>
                </div>
              ))}
              {(addonQuote.full_amount != null ||
                addonQuote.omitted_days != null) && (
                <div className="addon-breakdown__summary">
                  {addonQuote.full_amount != null && (
                    <div className="addon-breakdown__summary-row">
                      <span>
                        Total Cost
                        {addonQuote.cycle_days != null &&
                          ` (full ${addonQuote.cycle_days}-day cycle)`}
                      </span>
                      <span>
                        {formatMoney(
                          addonQuote.currency || addonCurrency,
                          addonQuote.full_amount,
                        )}
                      </span>
                    </div>
                  )}
                  {addonQuote.omitted_days != null && (
                    <div className="addon-breakdown__summary-row">
                      <span>Omitted Days (already passed, not charged)</span>
                      <span>{formatDays(addonQuote.omitted_days)}</span>
                    </div>
                  )}
                  <div className="addon-breakdown__summary-row addon-breakdown__summary-row--total">
                    <span>
                      You Pay
                      {addonQuote.remaining_days != null &&
                        ` (${formatDays(addonQuote.remaining_days)} left)`}
                    </span>
                    <span>
                      {formatMoney(
                        addonQuote.currency || addonCurrency,
                        addonQuote.amount,
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            className="modal-form"
            onValuesChange={(changed) => {
              if (changed.billing_cycle) {
                form.setFieldsValue({
                  amount: calculateAmount(changed.billing_cycle),
                });
              }
            }}
          >
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: "Amount is required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                prefix={
                  isAddon
                    ? addonCurrency
                    : selectedModulesData[0]?.currency || "JMD"
                }
                min={0}
                step={0.01}
                precision={2}
                placeholder="0.00"
                disabled
              />
            </Form.Item>

            <Form.Item
              name="payment_method"
              label="Payment Method"
              required={false}
              rules={[
                { required: true, message: "Payment method is required" },
              ]}
            >
              <Select
                placeholder="Select payment method"
                options={[
                  { label: "Cash", value: "cash" },
                  { label: "Bank Transfer", value: "bank_transfer" },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="payment_date"
              label="Payment Date"
              required={false}
              rules={[{ required: true, message: "Payment date is required" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabled={!watchedPaymentMethod}
                placeholder={
                  watchedPaymentMethod
                    ? "Select date"
                    : "Select payment method first"
                }
              />
            </Form.Item>

            <Form.Item
              name="billing_cycle"
              label="Billing Cycle"
              required={false}
              rules={[{ required: true, message: "Billing cycle is required" }]}
            >
              <Select
                placeholder="Select billing cycle"
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Annually", value: "annual" },
                ].map((option) => ({
                  ...option,
                  disabled: !!payCycle && option.value !== payCycle,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="proof_file"
              label="Proof of Payment"
              required={false}
              rules={[
                {
                  validator: () =>
                    proofFile || existingProofUrl
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("Proof of payment is required"),
                        ),
                },
              ]}
            >
              <div>
                <Button
                  icon={<UploadOutlined />}
                  onClick={handleUploadClick}
                  className="proof-upload-btn"
                  title={proofFile ? proofFile.name : undefined}
                >
                  <span className="proof-upload-btn__name">
                    {proofFile ? proofFile.name : "Upload Proof File"}
                  </span>
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                />
                {existingProofUrl && !proofFile && (
                  <div className="existing-proof-note">
                    <span>Current proof:</span>
                    <a
                      href={existingProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={getFileName(existingProofUrl)}
                    >
                      {getFileName(existingProofUrl)}
                    </a>
                  </div>
                )}
              </div>
            </Form.Item>
          </Form>

          {wipayTooSmall && resubmitId == null && (
            <div className="addon-breakdown__warning">
              WiPay needs at least JMD {WIPAY_MIN_AMOUNT.toFixed(2)}. Please use
              Submit Payment for this amount.
            </div>
          )}

          <div className="modal-footer-wrapper subscription-pay-footer">
            <Button onClick={handleCancelPay} disabled={isBusy}>
              Cancel
            </Button>
            {resubmitId == null && (
              <Button
                onClick={handleWiPayPayment}
                loading={wipayLoading}
                disabled={submitting || wipayTooSmall}
                title={
                  wipayTooSmall
                    ? `WiPay needs at least JMD ${WIPAY_MIN_AMOUNT.toFixed(2)}. Please use Submit Payment instead.`
                    : undefined
                }
              >
                Pay through WiPay
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleSubmitPayment}
              loading={submitting}
              disabled={wipayLoading}
            >
              Submit Payment
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Subscription;
